import { useState, useEffect, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { tennisStats, basketballStats, sports, statsFiltersBySport, leagueGroupsBySport } from '@/mocks/estadisticas';
import type { BasketballTeam } from '@/mocks/estadisticas-basketball';
import type { TennisPlayer } from '@/mocks/estadisticas-tennis';
import FootballTable, { type RealStanding } from '@/pages/estadisticas/components/FootballTable';
import TennisTable from '@/pages/estadisticas/components/TennisTable';
import BasketballTable from '@/pages/estadisticas/components/BasketballTable';
import DetailPanel, { type PanelItem } from '@/pages/estadisticas/components/DetailPanel';
import PlayersTab from '@/pages/estadisticas/components/PlayersTab';
import StatsProSection from '@/pages/estadisticas/components/StatsProSection';
import { useAuth } from '@/context/AuthContext';

const FOOTBALL_LEAGUE_SLUGS: Record<string, string> = {
  LaLiga: 'laliga',
  'Premier League': 'premier',
  'Serie A': 'seriea',
  Bundesliga: 'bundesliga',
  'Ligue 1': 'ligue1',
};

const SPORT_DEFAULT_FILTER: Record<string, string> = {
  Fútbol: 'goals',
  Tenis: 'serve',
  Baloncesto: 'scoring',
};

const SPORT_DEFAULT_LEAGUE: Record<string, string> = {
  Fútbol: 'Todas las ligas',
  Tenis: 'Todos los torneos',
  Baloncesto: 'Todas las ligas',
};

const COMPETITION_ICONS: Record<string, string> = {
  'Champions League': 'ri-star-line',
  'Europa League': 'ri-star-half-line',
  'Conference League': 'ri-shield-star-line',
  'Mundial FIFA': 'ri-earth-line',
  'UEFA Euro': 'ri-map-line',
  'Copa América': 'ri-cup-line',
  'Copa África': 'ri-sun-line',
  'Copa Asia': 'ri-compass-3-line',
  EuroCup: 'ri-shield-line',
  'FIBA World Cup': 'ri-earth-line',
  EuroBasket: 'ri-map-line',
  'Copa América FIBA': 'ri-cup-line',
  'Australian Open': 'ri-sun-line',
  'Roland Garros': 'ri-leaf-line',
  Wimbledon: 'ri-gradienter-line',
  'US Open': 'ri-flag-line',
  'Copa Davis': 'ri-team-line',
  'Copa Billie Jean King': 'ri-team-line',
};

function matchesBasketLeague(team: BasketballTeam, selectedLeague: string): boolean {
  if (selectedLeague === 'Todas las ligas') return true;
  return team.league === selectedLeague;
}

function matchesTennisTour(player: TennisPlayer, selectedLeague: string): boolean {
  if (selectedLeague === 'Todos los torneos') return true;
  return player.tour === selectedLeague;
}

// ── Premium lock overlay for advanced stats ──────────────────────────────────
function PremiumStatsLock({ onUpgrade }: { onUpgrade: () => void }) {
  return (
    <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-[#2a2d3a] flex items-center justify-between">
        <h3 className="text-white font-bold text-sm flex items-center gap-2">
          Estadísticas Avanzadas
          <span className="px-2 py-0.5 rounded-md bg-[#ffd700]/10 text-[#ffd700] text-xs font-bold">Premium</span>
        </h3>
      </div>
      <div className="relative">
        {/* Blurred preview rows */}
        <div className="select-none pointer-events-none opacity-30 blur-sm">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-5 py-4 border-b border-[#1e2028]">
              <div className="h-4 w-4 bg-[#2a2d3a] rounded text-center text-xs font-bold text-[#6b7280]">{i + 1}</div>
              <div className="flex-1 h-4 bg-[#2a2d3a] rounded max-w-[160px]"></div>
              <div className="ml-auto flex gap-6">
                <div className="h-4 w-10 bg-[#2a2d3a] rounded"></div>
                <div className="h-4 w-12 bg-[#2a2d3a] rounded"></div>
                <div className="h-4 w-10 bg-[#2a2d3a] rounded"></div>
                <div className="h-4 w-14 bg-[#2a2d3a] rounded"></div>
                <div className="h-4 w-10 bg-[#2a2d3a] rounded"></div>
              </div>
            </div>
          ))}
        </div>
        {/* Lock overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0d0f14]/80 backdrop-blur-[2px] p-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#ffd700]/15 mb-4">
            <i className="ri-vip-crown-line text-[#ffd700] text-2xl"></i>
          </div>
          <p className="text-white font-black text-lg text-center mb-2">Estadísticas Avanzadas</p>
          <p className="text-[#a8b2c1] text-sm text-center mb-1 max-w-xs leading-relaxed">
            Desbloquea con <strong className="text-[#ffd700]">Premium</strong> para ver:
          </p>
          <ul className="text-[#6b7280] text-xs text-center space-y-1">
            <li>Goles esperados (xG / xGA)</li>
            <li>Saques de esquina totales y en primera mitad</li>
            <li>% Ambos marcan (BTTS) y Over 2.5</li>
            <li>Posesión, tiros y precisión a puerta</li>
            <li>Puntos fuertes y débiles de rivales</li>
          </ul>
          <button
            onClick={onUpgrade}
            className="px-6 py-3 bg-[#ffd700] text-black font-black text-sm rounded-xl cursor-pointer hover:bg-[#f0c800] transition-all whitespace-nowrap"
          >
            Activar Premium — 2,50€/mes
          </button>
        </div>
      </div>
    </div>
  );
}

const STATS_BASE = import.meta.env.VITE_SITE_URL ?? 'https://example.com';
const STATS_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${STATS_BASE}/estadisticas`,
  url: `${STATS_BASE}/estadisticas`,
  name: 'Estadísticas Deportivas en Tiempo Real — Fútbol, Tenis, Baloncesto | Apuestamente',
  description: 'Estadísticas deportivas avanzadas en tiempo real: clasificaciones de fútbol, tenis y baloncesto. Datos oficiales de las 5 grandes ligas europeas vía ESPN.',
  inLanguage: 'es-ES',
  isPartOf: { '@id': `${STATS_BASE}/#website` },
};

export default function EstadisticasPage() {
  const { plan, isPremium, isRegistered, openPaywallModal, openLoginModal } = useAuth();

  const [selectedSport, setSelectedSport] = useState('Fútbol');
  const [selectedFilter, setSelectedFilter] = useState('goals');
  const [selectedLeague, setSelectedLeague] = useState('Todas las ligas');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [panelItem, setPanelItem] = useState<PanelItem | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [mainView, setMainView] = useState<'general' | 'pro'>('general');
  // New: tab view for football (teams vs players)
  const [footballView, setFootballView] = useState<'equipos' | 'jugadores'>('equipos');

  // Real standings state (equipos)
  const [realStandings, setRealStandings] = useState<Record<string, RealStanding[]>>({});
  const [loadingLeague, setLoadingLeague] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const openPanel = (item: PanelItem) => { setPanelItem(item); setPanelOpen(true); };
  const closePanel = () => { setPanelOpen(false); setTimeout(() => setPanelItem(null), 320); };

  const handleSportChange = (sport: string) => {
    setSelectedSport(sport);
    setSelectedFilter(SPORT_DEFAULT_FILTER[sport] ?? 'goals');
    setSelectedLeague(SPORT_DEFAULT_LEAGUE[sport] ?? 'Todas las ligas');
    setSearchQuery('');
    setFootballView('equipos');
    closePanel();
  };

  const handleUpgrade = () => {
    if (!isRegistered) openLoginModal();
    else openPaywallModal('premium');
  };

  // Fetch real standings for football leagues
  const fetchStandings = useCallback(async (league: string) => {
    const slug = FOOTBALL_LEAGUE_SLUGS[league];
    if (!slug) return;
    if (realStandings[slug]?.length > 0) return;

    const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
    if (!supabaseUrl) return;

    setLoadingLeague(slug);
    setLoadError(null);

    try {
      const res = await fetch(
        `${supabaseUrl}/functions/v1/football-stats?type=standings&league=${slug}&season=2025`
      );
      const data = await res.json();
      if (data.status === 'ok' && Array.isArray(data.data) && data.data.length > 0) {
        setRealStandings((prev) => ({ ...prev, [slug]: data.data }));
      } else {
        setLoadError('No se pudieron cargar los datos. Inténtalo de nuevo.');
      }
    } catch {
      setLoadError('Error de conexión al cargar los datos.');
    } finally {
      setLoadingLeague(null);
    }
  }, [realStandings]);

  useEffect(() => {
    fetchStandings('LaLiga');
  }, []);

  useEffect(() => {
    if (selectedSport === 'Fútbol' && selectedLeague !== 'Todas las ligas') {
      fetchStandings(selectedLeague);
    }
  }, [selectedLeague, selectedSport, fetchStandings]);

  useEffect(() => {
    document.title = 'Estadísticas Deportivas en Tiempo Real — Fútbol, Tenis, Baloncesto | Apuestamente';
    const id = 'schema-estadisticas';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(STATS_SCHEMA);
    return () => { document.getElementById(id)?.remove(); };
  }, []);

  const currentFilters = statsFiltersBySport[selectedSport] ?? statsFiltersBySport['Fútbol'];
  const leagueGroups = leagueGroupsBySport[selectedSport] ?? [];
  const defaultLeague = SPORT_DEFAULT_LEAGUE[selectedSport];

  const slug = FOOTBALL_LEAGUE_SLUGS[selectedLeague];
  const activeStandings: RealStanding[] = slug ? (realStandings[slug] ?? []) : [];
  const isLoadingCurrentLeague = slug ? loadingLeague === slug : false;

  const allStandingsFlat: RealStanding[] = selectedLeague === 'Todas las ligas'
    ? Object.entries(realStandings).flatMap(([s, rows]) =>
        rows.map((r) => ({
          ...r,
          team: r.team,
          league: Object.entries(FOOTBALL_LEAGUE_SLUGS).find(([, v]) => v === s)?.[0] ?? s,
        }))
      )
    : activeStandings;

  const footballDisplayData = allStandingsFlat.filter((t) =>
    !searchQuery || t.team.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTennis = tennisStats.filter((p) => {
    const matchSearch = !searchQuery || p.player.toLowerCase().includes(searchQuery.toLowerCase());
    return matchSearch && matchesTennisTour(p, selectedLeague);
  });

  const filteredBasketball = basketballStats.filter((t) => {
    const matchSearch = !searchQuery || t.team.toLowerCase().includes(searchQuery.toLowerCase());
    return matchSearch && matchesBasketLeague(t, selectedLeague);
  });

  const showFootballEmpty =
    selectedSport === 'Fútbol' &&
    !isLoadingCurrentLeague &&
    footballDisplayData.length === 0 &&
    !loadError;

  // Derive the league slug for PlayersTab based on current selection
  const playersLeagueSlug = selectedLeague !== 'Todas las ligas' && FOOTBALL_LEAGUE_SLUGS[selectedLeague]
    ? FOOTBALL_LEAGUE_SLUGS[selectedLeague]
    : 'laliga';

  return (
    <div className="bg-[#0d0f14] min-h-screen">
      <Navbar />
      <div className="pt-16">
        {/* Header */}
        <div className="bg-[#151719] border-b border-[#2a2d3a]">
          <div className="max-w-[1440px] mx-auto px-6 py-10">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <p className="text-[#00e87a] text-xs font-bold tracking-widest uppercase mb-2">Motor Estadístico</p>
                <h1 className="text-white text-4xl font-black uppercase">Estadísticas en Tiempo Real</h1>
                <p className="text-[#a8b2c1] text-sm mt-2">
                  Datos oficiales de las 5 grandes ligas · Equipos y jugadores · ESPN API
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#00e87a]/10 border border-[#00e87a]/20 text-[#00e87a] text-xs font-bold">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00e87a] animate-pulse inline-block"></span>
                  Datos reales · ESPN
                </span>
                {plan && (
                  <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                    plan === 'elite' ? 'bg-[#ff6b35]/15 text-[#ff6b35] border border-[#ff6b35]/30' :
                    plan === 'premium' ? 'bg-[#ffd700]/15 text-[#ffd700] border border-[#ffd700]/30' :
                    'bg-white/5 text-[#6b7280] border border-[#2a2d3a]'
                  }`}>
                    {plan === 'elite' ? 'Plan Elite' : plan === 'premium' ? 'Plan Premium' : 'Plan Gratuito'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-[1440px] mx-auto px-6 py-8">
          {/* Main view switcher */}
          <div className="flex gap-1 bg-[#151719] border border-[#2a2d3a] rounded-xl p-1 mb-6 w-fit">
            <button
              onClick={() => setMainView('general')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${mainView === 'general' ? 'bg-[#00e87a] text-black' : 'text-[#6b7280] hover:text-white'}`}
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-bar-chart-2-line text-sm"></i></div>
              Estadísticas
            </button>
            <button
              onClick={() => setMainView('pro')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${mainView === 'pro' ? 'bg-[#ffd700]/20 text-[#ffd700]' : 'text-[#6b7280] hover:text-white'}`}
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-database-2-line text-sm"></i></div>
              Stats Pro
              <span className="px-1.5 py-0.5 rounded-full bg-[#ffd700]/20 text-[#ffd700] text-[10px] font-black">
                {isPremium ? (plan === 'elite' ? 'ELITE' : 'PRO') : 'PREMIUM'}
              </span>
            </button>
          </div>

          {/* Stats Pro view */}
          {mainView === 'pro' && <StatsProSection />}

          {/* General stats view */}
          {mainView === 'general' && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-4">

              {/* Sport selector */}
              <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-4">
                <p className="text-[#6b7280] text-xs font-semibold uppercase tracking-wide mb-3">Deporte</p>
                <div className="flex flex-col gap-1">
                  {sports.map((s) => (
                    <button
                      key={s}
                      onClick={() => handleSportChange(s)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                        selectedSport === s ? 'bg-[#00e87a]/15 text-[#00e87a]' : 'text-[#a8b2c1] hover:text-white hover:bg-white/5'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* League filter */}
              <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-4">
                <p className="text-[#6b7280] text-xs font-semibold uppercase tracking-wide mb-3">
                  {selectedSport === 'Tenis' ? 'Tour / Circuito' : 'Liga / Competición'}
                </p>
                <div className="flex flex-col gap-0.5">
                  {leagueGroups.map(({ group, leagues }) => {
                    // Check if this group should show "Próximamente"
                    const isEuropeanOrNational = group.toLowerCase().includes('europe') || group.toLowerCase().includes('selecciones');
                    
                    return (
                      <div key={group || '_all'}>
                        {group !== '' && (
                          <div className="flex items-center gap-2 mt-3 mb-1.5">
                            {COMPETITION_ICONS[leagues[0]] && (
                              <div className="w-3 h-3 flex items-center justify-center text-[#ffd700]">
                                <i className={`${COMPETITION_ICONS[leagues[0]] ?? 'ri-trophy-line'} text-xs`}></i>
                              </div>
                            )}
                            <p className="text-[#ffd700] text-[10px] font-bold uppercase tracking-widest">{group}</p>
                          </div>
                        )}
                        {isEuropeanOrNational ? (
                          // Show "Próximamente" card for European competitions and National teams
                          <div className="bg-[#ff6b35]/5 border border-[#ff6b35]/20 rounded-lg p-3 mb-2">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-5 h-5 flex items-center justify-center rounded bg-[#ff6b35]/15">
                                <i className="ri-time-line text-[#ff6b35] text-xs"></i>
                              </div>
                              <span className="text-[#ff6b35] text-xs font-bold">Próximamente</span>
                            </div>
                            <p className="text-[#6b7280] text-[10px] leading-relaxed">
                              Estadísticas detalladas de {group.toLowerCase()} disponibles próximamente.
                            </p>
                          </div>
                        ) : (
                          // Normal league buttons
                          leagues.map((league) => (
                            <button
                              key={league}
                              onClick={() => setSelectedLeague(league)}
                              className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                                selectedLeague === league ? 'bg-[#ffd700]/15 text-[#ffd700]' : 'text-[#a8b2c1] hover:text-white hover:bg-white/5'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="truncate">{league}</span>
                                {selectedLeague === league && (
                                  <div className="w-3 h-3 flex items-center justify-center text-[#ffd700] flex-shrink-0">
                                    <i className="ri-check-line text-xs"></i>
                                  </div>
                                )}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Plan info sidebar */}
              {selectedSport === 'Fútbol' && !isPremium && (
                <div className="bg-[#ffd700]/5 border border-[#ffd700]/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 flex items-center justify-center text-[#ffd700]">
                      <i className="ri-vip-crown-line text-sm"></i>
                    </div>
                    <p className="text-[#ffd700] text-xs font-bold">Plan Premium</p>
                  </div>
                  <ul className="space-y-1.5 mb-3">
                    {[
                      'xG · xGA por partido',
                      'Corners 1H/2H',
                      'BTTS% · Over 2.5%',
                      'Últimos 10 partidos/jugador',
                      'Puntos fuertes/débiles',
                    ].map((f) => (
                      <li key={f} className="flex items-start gap-1.5 text-[#a8b2c1] text-xs">
                        <div className="w-3 h-3 flex items-center justify-center text-[#ffd700] mt-0.5 flex-shrink-0">
                          <i className="ri-lock-line text-xs"></i>
                        </div>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={handleUpgrade}
                    className="w-full py-2 bg-[#ffd700]/15 border border-[#ffd700]/30 text-[#ffd700] text-xs font-bold rounded-lg cursor-pointer hover:bg-[#ffd700]/25 transition-all whitespace-nowrap"
                  >
                    Activar — 2,50€/mes
                  </button>
                </div>
              )}

              {/* Stat filter (only for non-football or teams view) */}
              {(selectedSport !== 'Fútbol' || footballView === 'equipos') && (
                <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-4">
                  <p className="text-[#6b7280] text-xs font-semibold uppercase tracking-wide mb-3">Vista</p>
                  <div className="flex flex-col gap-1">
                    {currentFilters.map((f) => (
                      <button
                        key={f.id}
                        onClick={() => setSelectedFilter(f.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                          selectedFilter === f.id ? 'bg-[#00e87a]/15 text-[#00e87a]' : 'text-[#a8b2c1] hover:text-white hover:bg-white/5'
                        }`}
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`${f.icon} text-sm`}></i>
                        </div>
                        {f.label}
                        {!isPremium && f.id !== 'goals' && f.id !== 'serve' && f.id !== 'scoring' && (
                          <div className="ml-auto w-3 h-3 flex items-center justify-center text-[#ffd700]">
                            <i className="ri-lock-line text-xs"></i>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Advanced search */}
              {selectedSport === 'Fútbol' && isPremium && footballView === 'equipos' && (
                <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-4">
                  <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="w-full flex items-center justify-between text-sm font-semibold text-white cursor-pointer"
                  >
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center text-[#ffd700]">
                        <i className="ri-search-eye-line text-sm"></i>
                      </div>
                      Filtros Avanzados
                    </span>
                    <div className="w-4 h-4 flex items-center justify-center text-[#6b7280]">
                      <i className={`${showAdvanced ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-base`}></i>
                    </div>
                  </button>
                  {showAdvanced && (
                    <div className="mt-4 space-y-3">
                      <p className="text-[#6b7280] text-xs">Filtros avanzados disponibles en esta vista</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Main content */}
            <div className="lg:col-span-3 space-y-4">

              {/* Football: tab switcher between Equipos and Jugadores */}
              {selectedSport === 'Fútbol' && (
                <div className="flex gap-1 bg-[#151719] border border-[#2a2d3a] rounded-xl p-1 w-fit">
                  <button
                    onClick={() => setFootballView('equipos')}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                      footballView === 'equipos'
                        ? 'bg-[#00e87a] text-black'
                        : 'text-[#6b7280] hover:text-white'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-shield-line text-sm"></i>
                    </div>
                    Equipos
                  </button>
                  <button
                    onClick={() => setFootballView('jugadores')}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                      footballView === 'jugadores'
                        ? 'bg-[#00e87a] text-black'
                        : 'text-[#6b7280] hover:text-white'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-user-star-line text-sm"></i>
                    </div>
                    Jugadores
                    <span className="px-1.5 py-0.5 rounded-full bg-[#ff6b35]/15 text-[#ff6b35] text-[10px] font-black">
                      PRÓXIMAMENTE
                    </span>
                  </button>
                </div>
              )}

              {/* ── JUGADORES VIEW ────────────────────────────────────── */}
              {selectedSport === 'Fútbol' && footballView === 'jugadores' && (
                <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-12 text-center">
                  <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-[#ff6b35]/10 mx-auto mb-6">
                    <i className="ri-user-star-line text-[#ff6b35] text-3xl"></i>
                  </div>
                  <h3 className="text-white font-black text-xl mb-3">Estadísticas de Jugadores</h3>
                  <p className="text-[#a8b2c1] text-sm max-w-md mx-auto mb-6 leading-relaxed">
                    Próximamente: datos completos de jugadores con goles, asistencias, tarjetas, 
                    minutos jugados y rendimiento por partido de las 5 grandes ligas europeas.
                  </p>
                  <div className="flex items-center justify-center gap-2 text-[#6b7280] text-xs">
                    <i className="ri-time-line"></i>
                    <span>Disponible próximamente</span>
                  </div>
                </div>
              )}

              {/* ── EQUIPOS VIEW (original content) ──────────────────── */}
              {(selectedSport !== 'Fútbol' || footballView === 'equipos') && (
                <>
                  {/* Search + filters */}
                  <div className="flex gap-3 flex-wrap items-center">
                    <div className="flex-1 relative min-w-48">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#6b7280]">
                        <i className="ri-search-line text-base"></i>
                      </div>
                      <input
                        type="text"
                        placeholder={selectedSport === 'Tenis' ? 'Buscar jugador...' : 'Buscar equipo...'}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-[#151719] border border-[#2a2d3a] text-white text-sm rounded-xl pl-10 pr-4 py-3 placeholder-[#6b7280] focus:outline-none focus:border-[#00e87a]/40"
                      />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {selectedLeague !== defaultLeague && (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#ffd700]/10 border border-[#ffd700]/30 text-[#ffd700] text-xs font-bold">
                          <i className="ri-filter-line text-xs"></i>
                          {selectedLeague}
                          <button
                            onClick={() => setSelectedLeague(defaultLeague)}
                            className="cursor-pointer hover:text-white transition-colors ml-0.5"
                          >
                            <i className="ri-close-line text-xs"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Competition header */}
                  {selectedLeague !== defaultLeague && COMPETITION_ICONS[selectedLeague] && (
                    <div className="bg-[#ffd700]/5 border border-[#ffd700]/20 rounded-xl px-5 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#ffd700]/15 text-[#ffd700] flex-shrink-0">
                        <i className={`${COMPETITION_ICONS[selectedLeague]} text-base`}></i>
                      </div>
                      <div>
                        <p className="text-white font-bold text-sm">{selectedLeague}</p>
                        <p className="text-[#6b7280] text-xs">Estadísticas de equipos participantes</p>
                      </div>
                    </div>
                  )}

                  {/* Football section */}
                  {selectedSport === 'Fútbol' && (
                    <>
                      {selectedLeague === 'Todas las ligas' && footballDisplayData.length === 0 && !isLoadingCurrentLeague && (
                        <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl px-5 py-8 text-center">
                          <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#00e87a]/10 mx-auto mb-3">
                            <i className="ri-football-line text-[#00e87a] text-xl"></i>
                          </div>
                          <p className="text-white font-bold text-sm mb-1">Selecciona una liga</p>
                          <p className="text-[#6b7280] text-xs">
                            Elige LaLiga, Premier League, Serie A, Bundesliga o Ligue 1 para ver la clasificación real.
                          </p>
                        </div>
                      )}

                      {loadError && (
                        <div className="bg-[#ef4444]/10 border border-[#ef4444]/20 rounded-xl px-5 py-4 flex items-center gap-3">
                          <div className="w-4 h-4 flex items-center justify-center text-[#ef4444]">
                            <i className="ri-error-warning-line text-base"></i>
                          </div>
                          <p className="text-[#ef4444] text-sm">{loadError}</p>
                          <button
                            onClick={() => { setLoadError(null); if (selectedLeague !== 'Todas las ligas') fetchStandings(selectedLeague); }}
                            className="ml-auto text-xs text-[#6b7280] hover:text-white cursor-pointer whitespace-nowrap"
                          >
                            Reintentar
                          </button>
                        </div>
                      )}

                      {isLoadingCurrentLeague && (
                        <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl overflow-hidden animate-pulse">
                          <div className="px-5 py-4 border-b border-[#2a2d3a] flex items-center justify-between">
                            <div className="h-4 w-48 bg-[#2a2d3a] rounded"></div>
                            <div className="h-4 w-20 bg-[#2a2d3a] rounded"></div>
                          </div>
                          {Array.from({ length: 10 }).map((_, i) => (
                            <div key={i} className="flex items-center gap-4 px-5 py-4 border-b border-[#1e2028]">
                              <div className="h-4 w-5 bg-[#2a2d3a] rounded"></div>
                              <div className="h-8 w-8 bg-[#2a2d3a] rounded-full"></div>
                              <div className="h-4 w-36 bg-[#2a2d3a] rounded"></div>
                              <div className="ml-auto flex gap-4">
                                {Array.from({ length: 5 }).map((_, j) => (
                                  <div key={j} className="h-4 w-8 bg-[#2a2d3a] rounded"></div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {!isLoadingCurrentLeague && footballDisplayData.length > 0 && (
                        <FootballTable
                          data={footballDisplayData}
                          selectedId={panelItem?.sport === 'Fútbol' ? (panelItem.data as RealStanding).position : undefined}
                          onSelect={(team) => openPanel({ sport: 'Fútbol', data: team })}
                        />
                      )}


                    </>
                  )}

                  {/* Tennis */}
                  {selectedSport === 'Tenis' && (
                    <TennisTable
                      data={filteredTennis}
                      selectedFilter={selectedFilter}
                      selectedId={panelItem?.sport === 'Tenis' ? panelItem.data.id : undefined}
                      onSelect={(player) => openPanel({ sport: 'Tenis', data: player })}
                    />
                  )}

                  {/* Basketball */}
                  {selectedSport === 'Baloncesto' && (
                    <BasketballTable
                      data={filteredBasketball}
                      selectedFilter={selectedFilter}
                      selectedId={panelItem?.sport === 'Baloncesto' ? panelItem.data.id : undefined}
                      onSelect={(team) => openPanel({ sport: 'Baloncesto', data: team })}
                    />
                  )}

                  {/* Empty state */}
                  {showFootballEmpty && selectedLeague !== 'Todas las ligas' && (
                    <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-10 text-center">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 mx-auto mb-3">
                        <i className="ri-search-line text-[#6b7280] text-xl"></i>
                      </div>
                      <p className="text-white font-semibold text-sm">Sin resultados</p>
                      <p className="text-[#6b7280] text-xs mt-1">Prueba con otros filtros</p>
                    </div>
                  )}

                  {/* Plan comparison card */}
                  {selectedSport === 'Fútbol' && !isPremium && footballDisplayData.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {[
                        { plan: 'Gratuito', color: '#a8b2c1', bg: 'bg-white/5', border: 'border-[#2a2d3a]', features: ['Clasificación real en tiempo real', 'Últimos 5 resultados (forma)', 'xG y corners estimados', 'Estadísticas de jugadores (1 partido)'], locked: false, cta: null },
                        { plan: 'Premium', color: '#ffd700', bg: 'bg-[#ffd700]/5', border: 'border-[#ffd700]/20', features: ['Todo lo gratuito +', 'xG · Corners 1H/2H · BTTS%', 'Jugadores: últimos 10 partidos', 'Ligas ilimitadas · Gráficas avanzadas'], locked: true, cta: '2,50€/mes' },
                        { plan: 'Elite', color: '#ff6b35', bg: 'bg-[#ff6b35]/5', border: 'border-[#ff6b35]/20', features: ['Todo Premium +', 'Jugadores: historial completo', 'Exportar datos a Excel', 'Insignia exclusiva Elite'], locked: true, cta: '5,00€/mes' },
                      ].map((tier) => (
                        <div key={tier.plan} className={`${tier.bg} border ${tier.border} rounded-xl p-4`}>
                          <p className="font-bold text-sm mb-3" style={{ color: tier.color }}>{tier.plan}</p>
                          <ul className="space-y-1.5 mb-4">
                            {tier.features.map((f) => (
                              <li key={f} className="flex items-start gap-1.5 text-[#a8b2c1] text-xs">
                                <div className="w-3 h-3 flex items-center justify-center mt-0.5 flex-shrink-0" style={{ color: tier.color }}>
                                  <i className={tier.locked ? 'ri-lock-line text-xs' : 'ri-check-line text-xs'}></i>
                                </div>
                                {f}
                              </li>
                            ))}
                          </ul>
                          {tier.cta && (
                            <button
                              onClick={handleUpgrade}
                              className="w-full py-2 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap transition-all"
                              style={{ background: `${tier.color}20`, color: tier.color, border: `1px solid ${tier.color}40` }}
                            >
                              Activar {tier.cta}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          )}
        </div>
      </div>
      <Footer />
      <DetailPanel item={panelItem} isOpen={panelOpen} onClose={closePanel} />
    </div>
  );
}
